import {
  Box,
  Typography,
  Stack,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  ListItemIcon,
  FormControlLabel,
  TextField,
  Button
} from '@mui/material';
import {useContext, useEffect, useState, useRef} from 'react';
import { useParams } from 'react-router-dom';
import { AppContext } from '../../../context';
import { ROLE, IAccount } from '../../../resource';
import { useToastContext } from '../../../components/Toast/toastContext';
import { Pages } from '../../../contants';
import {contentItems} from '../../../components/Dialogs/WidgetList';

export const UserConfigurationPage = () => {
  const { id } = useParams<{ id: string }>();
  const {
    users,
  } = useContext(AppContext);
  const { activeToast } = useToastContext();
  const [formData, setFormData] = useState<Partial<IAccount>>({});
  const [isSaving, setIsSaving] = useState(false);
  const formDataRef = useRef<Partial<IAccount>>({});

  useEffect(() => {
    if (id) {
      users.fetchUser(id);
    }
  }, [id]);

  // Initialize form data when user data is loaded (only once when userToConfig first loads)
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (users.userToConfig && !hasInitialized.current) {
      const initialData = {
        role: users.userToConfig.role,
        allowedPages: users.userToConfig.allowedPages || [],
        allowedWidgets: users.userToConfig.allowedWidgets || [],
        canAddWidget: users.userToConfig.canAddWidget !== false,
        canRemoveWidget: users.userToConfig.canRemoveWidget !== false,
        widgetDeletePassword: '', // never show hash
        documentDeletePassword: '',
      };
      setFormData(initialData);
      formDataRef.current = initialData;
      hasInitialized.current = true;
    }
  }, [users.userToConfig]);

  // Reset initialization flag when id changes
  useEffect(() => {
    hasInitialized.current = false;
  }, [id]);

  // Update ref whenever formData changes
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const handleSave = async () => {
    const currentFormData = formDataRef.current;
    if (!users.userToConfig || !id) return;

    setIsSaving(true);
    try {
      const updateData: any = {
        ...users.userToConfig,
        role: currentFormData.role,
        allowedPages: currentFormData.allowedPages,
        allowedWidgets: currentFormData.allowedWidgets,
        canAddWidget: currentFormData.canAddWidget,
        canRemoveWidget: currentFormData.canRemoveWidget,
      };

      // Only include password if canRemoveWidget is enabled and password is provided
      if (currentFormData.canRemoveWidget) {
        // Only send password if it's been entered (not empty)
        if (currentFormData.widgetDeletePassword && currentFormData.widgetDeletePassword.trim() !== '') {
          updateData.widgetDeletePassword = currentFormData.widgetDeletePassword;
        }
        // empty field = leave existing hash unchanged
      } else {
        // If canRemoveWidget is disabled, clear the password
        updateData.widgetDeletePassword = null;
      }

      // Factsheet hard-delete protection password (hashes never returned to client)
      if (currentFormData.documentDeletePassword && currentFormData.documentDeletePassword.trim() !== '') {
        updateData.documentDeletePassword = currentFormData.documentDeletePassword;
      }

      await users.updateUser(id, updateData);
      activeToast({
        type: 'success',
        text: 'User configuration saved successfully',
      });
      // Clear password fields after successful save
      setFormData(prev => ({ ...prev, widgetDeletePassword: '', documentDeletePassword: '' }));
      formDataRef.current = { ...formDataRef.current, widgetDeletePassword: '', documentDeletePassword: '' };
    } catch (err: any) {
      activeToast({
        type: 'error',
        text: err.msg || 'Failed to save configuration',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRoleChange = (newRole: ROLE) => {
    setFormData(prev => ({ ...prev, role: newRole }));
  };

  const handlePagesChange = (event: any) => {
    const {
      target: { value },
    } = event;
    setFormData(prev => ({
      ...prev,
      allowedPages: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const handleWidgetsChange = (event: any) => {
    const { target: { value } } = event;
    setFormData(prev => ({
      ...prev,
      allowedWidgets: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const handleCanAddWidgetChange = (event: any) => {
    const { target: { checked } } = event;
    setFormData(prev => ({ ...prev, canAddWidget: checked }));
  };

  const handleCanRemoveWidgetChange = (event: any) => {
    const { target: { checked } } = event;
    setFormData(prev => ({
      ...prev,
      canRemoveWidget: checked,
      // Clear password if disabling
      widgetDeletePassword: checked ? prev.widgetDeletePassword : ''
    }));
  };

  const handleWidgetDeletePasswordChange = (event: any) => {
    const { target: { value } } = event;
    setFormData(prev => ({ ...prev, widgetDeletePassword: value || '' }));
  };

  const handleDocumentDeletePasswordChange = (event: any) => {
    const { target: { value } } = event;
    setFormData(prev => ({ ...prev, documentDeletePassword: value || '' }));
  };


  if (users.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!users.userToConfig) {
    return (
      <Box>
        <Typography variant="h6" color="error">User not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">User Configuration</Typography>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
          sx={{ minWidth: 120 }}
        >
          {isSaving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </Box>
      <Stack spacing={10} sx={{ maxWidth: 600 }}>
        <Box>
          <Typography color="text.secondary" variant="subtitle2">Name</Typography>
          <Typography variant="body1">{`${users.userToConfig.firstName || ''} ${users.userToConfig.lastName || ''}`}</Typography>
        </Box>
        <Box>
          <Typography color="text.secondary" variant="subtitle2">Email</Typography>
          <Typography variant="body1">{users.userToConfig.email}</Typography>
        </Box>
        <Box>
          <Typography color="text.secondary" variant="subtitle2">Customer ID</Typography>
          <Typography variant="body1">{users.userToConfig.id}</Typography>
        </Box>
        <Box>
          <Typography color="text.secondary" variant="subtitle2" sx={{ mb: 1 }}>Access Level</Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={formData.role || ''}
              onChange={(e) => handleRoleChange(e.target.value as ROLE)}
            >
              <MenuItem value={ROLE.ADMIN}>Admin</MenuItem>
              <MenuItem value={ROLE.CUSTOMER}>Customer</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {formData.role === ROLE.CUSTOMER && (
          <Box>
            <Typography color="text.secondary" variant="subtitle2" sx={{ mb: 1 }}>Allowed Pages</Typography>
            <FormControl sx={{ minWidth: 200 }}>
              <Select
                multiple
                value={formData.allowedPages || []}
                onChange={handlePagesChange}
                renderValue={(selected) => `${(selected as string[]).length} page${(selected as string[]).length !== 1 ? 's' : ''} allowed`}
              >
                {Pages.map((page) => (
                  <MenuItem key={page} value={page}>
                    <Checkbox checked={(formData.allowedPages || []).indexOf(page) > -1} />
                    <ListItemText primary={page} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
        {formData.role === ROLE.CUSTOMER && (
          <Box>
            <Typography color="text.secondary" variant="subtitle2" sx={{ mb: 1 }}>Allowed Widgets</Typography>
            <FormControl sx={{ minWidth: 200 }}>
              <Select
                multiple
                value={formData.allowedWidgets || []}
                onChange={handleWidgetsChange}
                renderValue={(selected) => `${(selected as string[]).length} widget${(selected as string[]).length !== 1 ? 's' : ''} allowed`}
              >
                {contentItems.map((widget) => (
                  <MenuItem key={widget.type} value={widget.type} sx={{}}>
                    <Checkbox checked={(formData.allowedWidgets || []).indexOf(widget.type) > -1} />
                    <ListItemText primary={widget.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.canAddWidget !== false}
                onChange={handleCanAddWidgetChange}
              />
            }
            label="Allow user to add new widgets in Manage Widgets page"
          />
        </Box>
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.canRemoveWidget !== false}
                onChange={handleCanRemoveWidgetChange}
              />
            }
            label="Allow user to remove widgets in Manage Widgets page"
          />
          {formData.canRemoveWidget !== false && (
            <Box sx={{ mt: 2, ml: 4 }}>
              <TextField
                type="password"
                label="Widget Delete Password"
                value={formData.widgetDeletePassword || ''}
                onChange={handleWidgetDeletePasswordChange}
                placeholder={users.userToConfig.hasWidgetDeletePassword ? '•••••••• (set new to replace)' : 'Enter password for widget deletion'}
                helperText={users.userToConfig.hasWidgetDeletePassword ? 'A password is set. Leave blank to keep it.' : undefined}
                size="small"
                sx={{ minWidth: 300 }}
              />
            </Box>
          )}
        </Box>
        <Box>
          <Typography color="text.secondary" variant="subtitle2" sx={{ mb: 1 }}>
            Factsheet deletion protection
          </Typography>
          <TextField
            type="password"
            label="Factsheet Hard-Delete Password"
            value={formData.documentDeletePassword || ''}
            onChange={handleDocumentDeletePasswordChange}
            placeholder={users.userToConfig.hasDocumentDeletePassword ? '•••••••• (set new to replace)' : 'Required before permanent delete'}
            helperText={
              users.userToConfig.hasDocumentDeletePassword
                ? 'A password is set. Leave blank to keep it. Permanent delete is fail-closed without it.'
                : 'Customers cannot permanently delete factsheets until this is set.'
            }
            size="small"
            sx={{ minWidth: 300 }}
          />
        </Box>
        {users.userToConfig.companyName && (
          <Box>
            <Typography color="text.secondary" variant="subtitle2">Company</Typography>
            <Typography variant="body1">{users.userToConfig.companyName}</Typography>
          </Box>
        )}
        {users.userToConfig.companyUrl && (
          <Box>
            <Typography color="text.secondary" variant="subtitle2">Company URL</Typography>
            <Typography variant="body1">{users.userToConfig.companyUrl}</Typography>
          </Box>
        )}
        {users.userToConfig.phone && (
          <Box>
            <Typography color="text.secondary" variant="subtitle2">Phone</Typography>
            <Typography variant="body1">{users.userToConfig.phone}</Typography>
          </Box>
        )}
        {users.userToConfig.address && (
          <Box>
            <Typography color="text.secondary" variant="subtitle2">Address</Typography>
            <Typography variant="body1">{users.userToConfig.address}</Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
};
